/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import toast from 'react-hot-toast';
import { getAllLabel, getAllVisibleTo, getCurrency } from '../../Redux/CommonSlice';
import { addORUpdateLeadMaster } from '../../Redux/LeadsSlice';
import { getAllPerson, handleAddPerson } from '../../Redux/PersonSlice';
import { fetchApiData, saveApiData } from '../../Redux/organizationSlice';
import { deleteDeal } from '../../Redux/DealSlice';
import ReactSelect from '../Common/ReactSelect';
import AddLabel from '../Common/AddLabel';
import { formatNumber, LeadStatus, PREFERRED_CONTACT_METHODS, sourceChannel } from '../Common/Common';
import { AddRowBtn, DeleteBtn } from '../Common/Buttons';

// ── Constants ──────────────────────────────────────────────────────────────
const DEFAULT_CURRENCY = { value: 63, label: 'Indian Rupee(INR)' };
const EMPTY_PHONE = { phone: '', type: '', country: 'in' };
const EMPTY_EMAIL = { email: '', type: '' };

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// ── Sub-components ─────────────────────────────────────────────────────────
const Field = ({ label, error, required, children }) => (
    <div className="lead-modal-field">
        <label className="lead-modal-label">
            {label}
            {required && <span className="lead-modal-required" aria-hidden="true"> *</span>}
        </label>
        {children}
        {error && <p className="lead-modal-error">{error}</p>}
    </div>
);

const TypeSelect = ({ value, onChange, options }) => (
    <select className="lead-modal-type-select" value={value} onChange={onChange}>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
);
// ── Validation ─────────────────────────────────────────────────────────────
const validate = (formData, selectedPerson, selectedOrganization, allPhone, allEmail) => {
    const errors = {};

    if (!formData.title) errors.title = 'Full Name is required';
    if (!selectedPerson?.value) errors.person = 'Person is required';
    if (!selectedOrganization?.value) errors.organization = 'Company is required';

    // Phone validation
    const phoneCounts = allPhone.reduce((acc, { phone }) => {
        acc[phone] = (acc[phone] || 0) + 1;
        return acc;
    }, {});

    allPhone.forEach(({ phone, country }, i) => {
        const len = phone?.trim()?.length;
        const dialLen = country?.length;
        if (!phone) {
            errors[`Phone-${i}`] = 'Phone number is required';
        } else if (
            (dialLen === 1 && len <= 10) ||
            (dialLen === 2 && len <= 11) ||
            (dialLen === 3 && (len <= 10 || !/^\d+$/.test(phone)))
        ) {
            errors[`Phone-${i}`] = 'Invalid phone number';
        } else if (phoneCounts[phone] > 1 && allPhone.findIndex(p => p.phone === phone) !== i) {
            errors[`Phone-${i}`] = 'Phone number already exists';
        }
    });

    // Email validation
    const emailCounts = allEmail.reduce((acc, { email }) => {
        acc[email] = (acc[email] || 0) + 1;
        return acc;
    }, {});

    allEmail.forEach(({ email }, i) => {
        const trimmed = email?.trim();
        if (!trimmed) {
            errors[`Email-${i}`] = 'Email is required';
        } else if (!emailRegex.test(trimmed)) {
            errors[`Email-${i}`] = 'Invalid email address';
        } else if (emailCounts[trimmed] > 1 && allEmail.findIndex(e => e.email === trimmed) !== i) {
            errors[`Email-${i}`] = 'Email already exists';
        }
    });

    return errors;
};

// ── Main Component ─────────────────────────────────────────────────────────
const AddLeads = ({ togglemodelLead, leadData, setLoadFistInbox }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { common: store, Person: Peoplestore, organization: orgStore } =
        useSelector((s) => s.root);

    const [formData, setFormData] = useState({
        title: '', value: '', ownerID: '', expectedCloseDate: '', sourceChannelID: 0, jobTitle: '', website: '', notes: '', address: '', ...leadData
    });
    const [allPhone, setAllPhone] = useState([EMPTY_PHONE]);
    const [allEmail, setAllEmail] = useState([EMPTY_EMAIL]);
    const [selectedPerson, setSelectedPerson] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState(DEFAULT_CURRENCY);
    const [selectedLabel, setSelectedLabel] = useState(0);
    const [selectedVisible, setSelectedVisible] = useState(1);
    const [sourceChannel_, setSourceChannel] = useState('');
    const [selectedLeadStatus, setSelectedLeadStatus] = useState('');
    const [selectedContactMethod, setSelectedContactMethod] = useState({ value: 1, label: 'Email' });
    const [openLabelModel, setOpenLabelModel] = useState(false);
    const [errors, setErrors] = useState({});
    const [creatingPerson, setCreatingPerson] = useState(false);
    const [creatingOrganization, setCreatingOrganization] = useState(false);

    // Initial data fetch
    useEffect(() => {
        dispatch(getCurrency({}));
        dispatch(getAllLabel({}));
        dispatch(getAllVisibleTo({}));
        dispatch(getAllPerson({}));
        dispatch(fetchApiData({}));
    }, [dispatch]);

    // Populate form when editing
    useEffect(() => {
        if (!leadData) return;
        setFormData(prev => ({ ...prev, ...leadData }));

        const { sourceChannel: sc, leadEmailDetails, dealEmailDetails,
            leadPhoneDetails, dealPhoneDetails, expectedCloseDate: ecd,
            organizationID, organization, peopleID, person,
            labelID, labelName, visibleID, visibleName, currencyID, currencyName,
            leadStatus, leadStatusName, preferredContact } = leadData;

        if (sc) setSourceChannel({ label: sc, value: sc });
        if (leadEmailDetails) setAllEmail(leadEmailDetails.map(e => ({ email: e.email, type: e.emailType })));
        if (dealEmailDetails) setAllEmail(dealEmailDetails.map(e => ({ email: e.email, type: e.emailType })));
        if (leadPhoneDetails) setAllPhone(leadPhoneDetails.map(p => ({ phone: p.phoneNumber, type: p.numberType })));
        if (dealPhoneDetails) setAllPhone(dealPhoneDetails.map(p => ({ phone: p.phoneNumber, type: p.numberType })));
        if (organizationID) setSelectedOrganization({ label: organization?.organizationName, value: organizationID });
        if (peopleID) setSelectedPerson({ label: person?.name, value: peopleID });
        if (labelID) setSelectedLabel({ label: labelName, value: labelID });
        if (visibleID) setSelectedVisible({ label: visibleName, value: visibleID });
        if (currencyID) setSelectedCurrency({ label: currencyName, value: currencyID });
        if (leadStatus) setSelectedLeadStatus({ label: leadStatusName, value: leadStatus });
        if (preferredContact) {
            const found = PREFERRED_CONTACT_METHODS.find(m => m.label === preferredContact);
            if (found) setSelectedContactMethod(found);
        }
    }, [leadData]);

    // Enrich currency icon
    useEffect(() => {
        if (!store?.getCurrency || selectedCurrency?.icon) return;
        const found = store.getCurrency.find(x => x.value === selectedCurrency?.value);
        if (found?.type) setSelectedCurrency(prev => ({ ...prev, icon: found.type }));
    }, [store?.getCurrency, selectedCurrency?.value]);

    // ── Handlers ──
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'value' ? value.replace(/[^0-9]/g, '') : value,
        }));
        clearError(name);
    };

    const updatePhone = (index, patch) =>
        setAllPhone(prev => prev.map((p, i) => i === index ? { ...p, ...patch } : p));

    const updateEmail = (index, patch) =>
        setAllEmail(prev => prev.map((e, i) => i === index ? { ...e, ...patch } : e));

    const clearError = (key) => setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });

    const resolveCreatedPersonId = (res, name, personList) => {
        const data = res?.payload?.data;
        if (data?.personMasterID) return data.personMasterID;
        if (typeof data === 'number') return data;
        const match = [...(personList || [])]
            .reverse()
            .find((p) => p.name?.toLowerCase() === name.toLowerCase());
        return match?.personMasterID;
    };

    const resolveCreatedOrgId = (res, name, orgList) => {
        const data = res?.payload?.data;
        if (data?.organizationID) return data.organizationID;
        if (typeof data === 'number') return data;
        const match = [...(orgList || [])]
            .reverse()
            .find((o) => o.organizationName?.toLowerCase() === name.toLowerCase());
        return match?.organizationID;
    };

    const handleCreatePerson = async (name) => {
        const trimmed = name?.trim();
        if (!trimmed) return;

        setCreatingPerson(true);
        try {
            const labelId = typeof selectedLabel === 'object' ? selectedLabel?.value : selectedLabel;
            const visibleId = typeof selectedVisible === 'object' ? selectedVisible?.value : selectedVisible;

            const params = {
                personMasterID: 0,
                name: trimmed,
                organizationID: selectedOrganization?.value || 0,
                labelID: labelId ?? 0,
                ownerID: 0,
                visibleToID: visibleId ?? 1,
                userID: 0,
                personPhoneDetails: allPhone
                    .filter((p) => p.phone?.trim())
                    .map((p) => ({
                        personPhoneDetailsID: 0,
                        personMasterID: 0,
                        phoneNumber: p.phone,
                        phoneType: p.type || 'Home',
                    })),
                personEmailDetails: allEmail
                    .filter((e) => e.email?.trim())
                    .map((e) => ({
                        personEmailDetailsID: 0,
                        personMasterID: 0,
                        email: e.email,
                        emailType: e.type || 'Home',
                    })),
            };

            const res = await dispatch(handleAddPerson(params));
            if (res?.payload?.status !== true) return;

            const listRes = await dispatch(getAllPerson({}));
            const personList = listRes?.payload?.data ?? [];
            const newId = resolveCreatedPersonId(res, trimmed, personList);

            if (newId) {
                setSelectedPerson({ value: newId, label: trimmed });
                clearError('person');
            }
        } finally {
            setCreatingPerson(false);
        }
    };

    const handleCreateOrganization = async (name) => {
        const trimmed = name?.trim();
        if (!trimmed) return;

        setCreatingOrganization(true);
        try {
            const labelId = typeof selectedLabel === 'object' ? selectedLabel?.value : selectedLabel;
            const visibleId = typeof selectedVisible === 'object' ? selectedVisible?.value : selectedVisible;

            const payload = {
                organizationID: 0,
                organizationName: trimmed,
                userID: 0,
                labelId: labelId ?? 0,
                ownerId: 0,
                address: '',
                visibleToId: visibleId ?? 1,
            };

            const res = await dispatch(saveApiData(payload));
            if (res?.payload?.status !== true) return;

            const listRes = await dispatch(fetchApiData({}));
            const orgList = listRes?.payload?.data ?? [];
            const newId = resolveCreatedOrgId(res, trimmed, orgList);

            if (newId) {
                setSelectedOrganization({ value: newId, label: trimmed });
                clearError('organization');
            }
        } finally {
            setCreatingOrganization(false);
        }
    };

    const closeModal = () => {
        togglemodelLead();
        setFormData({ title: '', value: '', ownerID: '' });
        setErrors({});
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validate(formData, selectedPerson, selectedOrganization, allPhone, allEmail);
        if (Object.keys(validationErrors).length) return setErrors(validationErrors);

        const payload = {
            leadID: leadData?.leadID ?? 0,
            peopleID: selectedPerson?.value,
            organizationID: selectedOrganization?.value,
            title: formData.title,
            jobTitle: formData.jobTitle,
            value: formData.value,
            currencyID: selectedCurrency?.value,
            labelID: selectedLabel?.value,
            ownerID: 0,
            // ExpectedCloseDate: expectedCloseDate,
            visibleID: selectedVisible?.value,
            sourceType: 'Manually created',
            sourceChannel: sourceChannel_?.label,
            leadStatus: selectedLeadStatus?.value,
            website: formData.website,
            notes: formData.notes,
            leadAddress: formData.address,
            preferredContact: selectedContactMethod?.value,
            leadPhoneDetails: allPhone.map(p => ({ leadPhoneDetailID: 0, leadMasterID: 0, phoneNumber: p.phone, numberType: p.type })),
            leadEmailDetails: allEmail.map(e => ({ leadEmailDetailID: 0, leadMasterID: 0, email: e.email, emailType: e.type })),
        };

        if (leadData?.dealID) {
            dispatch(deleteDeal(leadData.dealID));
            navigate('/Deals');
        }

        dispatch(addORUpdateLeadMaster(payload)).then((res) => {
            toast.remove();
            if (res.payload?.status === true) {
                setLoadFistInbox(true);
                toast.success(leadData?.dealID ? 'Deal converted to lead successfully!' : res.payload.message);
            }
            closeModal();
        });
    };

    // ── Derived data ──
    const personOptions = Peoplestore?.getAllPerson?.map(p => ({ value: p.personMasterID, label: p.name })) ?? [];
    const orgOptions = orgStore?.organizationData?.data?.map(o => ({ value: o.organizationID, label: o.organizationName })) ?? [];
    const currencyOptions = store?.getCurrency?.map(c => ({ value: c.value, label: c.text, icon: c.type })) ?? [];
    const labelOptions = store?.getAllLabel?.map(l => ({ value: l.value, label: l.text })) ?? [];
    const visibleOptions = store?.getAllVisibleTo?.map(v => ({ value: v.value, label: v.text })) ?? [];
    const sourceOptions = sourceChannel?.map(s => ({ value: s.id, label: s.name })) ?? [];
    const LeadStatusOptions = LeadStatus?.map(s => ({ value: s.value, label: s.label })) ?? [];

    const modalTitle = leadData?.leadID ? 'Update lead' : leadData?.dealID ? 'Convert to lead' : 'Add lead';

    return (
        <>
            <div id="default-modal" tabIndex="-1" aria-hidden="true" className="lead-modal-backdrop">
                <div className="lead-modal-shell">
                    <div className="lead-modal-card">

                        {/* Header */}
                        <div className="lead-modal-header">
                            <h3 className="lead-modal-title">{modalTitle}</h3>
                            <AiOutlineCloseCircle className="lead-modal-close" onClick={closeModal} />
                        </div>

                        <form onSubmit={onSubmit}>
                            <div className="lead-modal-body">
                                <div className="lead-modal-grid vertical-scroll-inner">

                                    {/* ── Left: Lead details ── */}
                                    <div className="lead-modal-section">
                                        <div className="lead-modal-section-header">
                                            <span className="lead-modal-section-dot" />
                                            <h4>Lead details</h4>
                                        </div>
                                        <div className="lead-modal-section-content">
                                            <Field label="Full Name" required error={errors.title}>
                                                <input type="text" name="title" className="lead-modal-input capitalize"
                                                    placeholder="e.g. Priya Sharma"
                                                    value={formData.title} onChange={handleChange} maxLength={50} />
                                            </Field>
                                            <div className='lead-modal-row'>

                                                <Field label="Company" required error={errors.organization}>
                                                    <ReactSelect
                                                        creatable
                                                        createEntityName="company"
                                                        isLoading={creatingOrganization}
                                                        selectedValue={selectedOrganization}
                                                        options={orgOptions}
                                                        handleSelectChange={(opt) => {
                                                            setSelectedOrganization(opt);
                                                            clearError('organization');
                                                        }}
                                                        onCreateOption={handleCreateOrganization}
                                                    />
                                                </Field>
                                                <Field label="Job Title">
                                                    <input type="text" name="jobTitle" className="lead-modal-input"
                                                        placeholder="e.g. Procurement Manager"
                                                        value={formData?.jobTitle} onChange={handleChange} />
                                                </Field>
                                            </div>
                                            <div className='lead-modal-row'>
                                                <Field label="Lead Status">
                                                    <ReactSelect
                                                        selectedValue={selectedLeadStatus}
                                                        options={LeadStatusOptions}
                                                        handleSelectChange={(opt) => {
                                                            setSelectedLeadStatus(opt);
                                                        }}
                                                    />
                                                </Field>
                                                <Field label="Estimated Deal Value">
                                                    <input type="text" name="value" className="lead-modal-input"
                                                        placeholder="0.00"
                                                        value={formatNumber(formData.value)} onChange={handleChange} />
                                                </Field>
                                            </div>
                                            <Field label="Contact person" required error={errors.person}>
                                                <ReactSelect
                                                    creatable
                                                    createEntityName="contact"
                                                    isLoading={creatingPerson}
                                                    selectedValue={selectedPerson}
                                                    options={personOptions}
                                                    handleSelectChange={(opt) => {
                                                        setSelectedPerson(opt);
                                                        clearError('person');
                                                    }}
                                                    onCreateOption={handleCreatePerson}
                                                />
                                            </Field>
                                            <Field label="Currency">
                                                <ReactSelect Clearable={false} selectedValue={selectedCurrency}
                                                    options={currencyOptions} handleSelectChange={setSelectedCurrency} />
                                            </Field>

                                            <div className="lead-modal-row">
                                                {/* <Field label="Source channel ID">
                                                    <input type="number" name="sourceChannelID" className="lead-modal-input"
                                                        value={formData.sourceChannelID} onChange={handleChange} min="0" />
                                                </Field> */}
                                                <Field label="Source channel">
                                                    <ReactSelect selectedValue={sourceChannel_}
                                                        options={sourceOptions} handleSelectChange={setSourceChannel} />
                                                </Field>
                                                <Field label="Labels">
                                                    <div className="lead-modal-inline-control">
                                                        <ReactSelect selectedValue={selectedLabel}
                                                            options={labelOptions} handleSelectChange={setSelectedLabel} />
                                                        <AddRowBtn
                                                            label="Add lable"
                                                            onClick={() => setOpenLabelModel(true)}
                                                        />
                                                    </div>
                                                </Field>
                                            </div>

                                            {/* <Field label="Owner">
                                                <input as="select" name="ownerID" className="lead-modal-input" onChange={handleChange} />
                                            </Field> */}

                                            <Field label="Website">
                                                <input type="text" name="website" className="lead-modal-input"
                                                    placeholder="https://example.com"
                                                    value={formData.website} onChange={handleChange} />
                                            </Field>
                                            <Field label="Visible to">
                                                <ReactSelect selectedValue={selectedVisible}
                                                    options={visibleOptions} handleSelectChange={setSelectedVisible} />
                                            </Field>
                                            <Field label="Notes">
                                                <textarea name="notes" className="lead-modal-input" rows={3}
                                                    placeholder="Any additional context about this lead..."
                                                    value={formData.notes} onChange={handleChange}
                                                    style={{ resize: 'vertical', minHeight: '80px' }} />
                                            </Field>
                                        </div>
                                    </div>

                                    {/* ── Right: Contact ── */}
                                    <div className="lead-modal-section bg-transparent">
                                        <div className="lead-modal-section-header">
                                            <span className="lead-modal-section-dot lead-modal-section-dot-blue" />
                                            <h4>Person contact</h4>
                                        </div>
                                        <div className="lead-modal-section-content">

                                            {/* Phone */}
                                            <Field label="Phone" required>
                                                <div className="lead-modal-stack">
                                                    {allPhone.map((item, i) => (
                                                        <div key={i} className="lead-modal-contact-row">
                                                            <div className="lead-modal-contact-input">
                                                                <PhoneInput
                                                                    country="in"
                                                                    value={item.phone}
                                                                    onChange={(val, country) => {
                                                                        updatePhone(i, { phone: val, country: country.dialCode });
                                                                        clearError(`Phone-${i}`);
                                                                    }}
                                                                    containerClass="lead-phone-container"
                                                                    inputClass="lead-phone-input"
                                                                    buttonClass="lead-phone-button"
                                                                    inputProps={{ required: true }}
                                                                />
                                                                {errors[`Phone-${i}`] && (
                                                                    <p className="lead-modal-error">{errors[`Phone-${i}`]}</p>
                                                                )}
                                                            </div>
                                                            {allPhone.length > 1 && (
                                                                <DeleteBtn
                                                                    onClick={() =>setAllPhone((prev) => prev.filter((_, idx) => idx !== i))}
                                                                />
                                                            )}
                                                            {i === allPhone.length - 1 && (
                                                                <AddRowBtn
                                                                    label="Phone"
                                                                    onClick={() => setAllPhone((prev) => [...prev, EMPTY_PHONE])}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </Field>

                                            {/* Email */}
                                            <Field label="Email" required>
                                                <div className="lead-modal-stack">
                                                    {allEmail.map((item, i) => (
                                                        <div key={i} className="lead-modal-contact-row">
                                                            <div className="lead-modal-contact-input">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Enter email"
                                                                    className="lead-modal-input"
                                                                    value={item.email || ''}
                                                                    onChange={(e) => {
                                                                        updateEmail(i, { email: e.target.value });
                                                                        clearError(`Email-${i}`);
                                                                    }}
                                                                />
                                                                {errors[`Email-${i}`] && (
                                                                    <p className="lead-modal-error">{errors[`Email-${i}`]}</p>
                                                                )}
                                                            </div>
                                                            {allEmail.length > 1 && (
                                                                <DeleteBtn
                                                                    onClick={() =>
                                                                        setAllEmail((prev) => prev.filter((_, idx) => idx !== i))
                                                                    }
                                                                />
                                                            )}
                                                            {i === allEmail.length - 1 && (
                                                                <AddRowBtn
                                                                    label="Email"
                                                                    onClick={() => setAllEmail((prev) => [...prev, EMPTY_EMAIL])}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </Field>

                                            {/* Address */}
                                            <Field label="Address">
                                                <input type="text" name="address" className="lead-modal-input"
                                                    placeholder="City, State, Country"
                                                    value={formData.address} onChange={handleChange} />
                                            </Field>

                                            {/* Preferred contact method */}
                                            <Field label="Preferred contact method">
                                                <ReactSelect
                                                    selectedValue={selectedContactMethod}
                                                    options={PREFERRED_CONTACT_METHODS}
                                                    handleSelectChange={setSelectedContactMethod}
                                                    Clearable={false}
                                                />
                                            </Field>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="lead-modal-footer">
                                <button type="button"
                                    className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-semibold rounded-lg transition-colors cursor-pointer mr-2"
                                    onClick={closeModal}>
                                    Close
                                </button>
                                <button type="submit"
                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer">
                                    {leadData?.leadID ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {openLabelModel && (
                <AddLabel setOpenLabelModel={setOpenLabelModel} openLabelModel={openLabelModel} />
            )}
        </>
    );
};

export default AddLeads;
